/**
 * API 응답 ErrCode → Sentry 수집 정책
 *
 * fetcher에서 모든 API 응답 후 captureApiResponseError(Header) 호출.
 * CmdType으로 flow를 분류하고, ErrCode·flow 조합에 따라 level을 결정한다.
 *
 * level 요약 (수집 대상만):
 * - Error   : CRITICAL_ERR_CODES (시스템 장애)
 * - Warning : payment / auth / join / cancel / session flow의 예상 밖 ErrCode
 * - Info    : other flow의 예상 밖 ErrCode
 */
import { CMDTYPES } from 'src/service/constant/cmdtypes'
import {
  ERRCODE_SUCCESS,
  ERRCODES_COMMON,
  ERRCODES_GIFT_VALID_CHECK,
  ERRCODES_OTPCERTREQUEST,
  ERRCODES_OTPCERTSUBMIT,
  ERRCODES_PASS_CERT,
  ERRCODES_PURCHASE_CANCEL,
  ERRCODES_PURCHASE_REFUND,
  ERRCODES_PURCHASE_VALID_CHECK,
  ERRCODES_VERSIONCHECK,
  ERRCODES_WALLETCOUPON,
} from 'src/service/constant/errcodes'
import { captureError, SENTRY_SEVERITY, toApiError } from 'src/utils/sentry'

// ─────────────────────────────────────────────
// CmdType → flow 분류
// ─────────────────────────────────────────────

type SentryApiFlow = 'payment' | 'auth' | 'join' | 'cancel' | 'session' | 'other'

const PAYMENT_CMDTYPES = new Set<number>([
  CMDTYPES.PaymentGatewayRequest, // 카드 결제 PG사 요청 - 99
  CMDTYPES.PaymentWindowCreditCardRequest, // 결제창 요청 정보 저장 - 100
  CMDTYPES.PaymentWindowCreditCardResponse, // 결제 결과 - 101
  CMDTYPES.PurchaseValidCheck, // 구매시 유효성 검사 - 1044
  CMDTYPES.PurchaseCouponCancel, // 쿠폰 구매 취소 - 1045
  CMDTYPES.RefundSubmit, // 쿠폰 환불 - 1046
  CMDTYPES.RefundTelecomCertFileSubmit, // 텔레콤증명파일 업로드 - 1047
  CMDTYPES.CancelCouponValidCheck, // 쿠폰 구매 취소기간 체크 - 1049
])

const AUTH_CMDTYPES = new Set<number>([
  CMDTYPES.PassCertCheck, // PASS 인증 완료 여부 - 2000
  CMDTYPES.PassDataEncryptRequest, // PASS데이터 암호화 요청 - 200
  CMDTYPES.PassCertResponse, // PASS 본인인증 결과확인 - 201
])

const JOIN_CMDTYPES = new Set<number>([
  CMDTYPES.OtpCertRequest, // 인증 번호 요청 - 1004
  CMDTYPES.OtpCertSubmit, // 인증 번호 검증 - 1005
  CMDTYPES.TermsList, // 약관 목록 - 1006
  CMDTYPES.Join, // 가입 - 1007
  CMDTYPES.KTMVNO, // KT MVNO 확인 - 1012
  CMDTYPES.CheckVulerable, // 취약계층 여부 확인 - 2025
])

const CANCEL_CMDTYPES = new Set<number>([
  CMDTYPES.Cancel, // 해지 - 1010
])

const SESSION_CMDTYPES = new Set<number>([
  CMDTYPES.SessionCheck, // SessionCheck - 1002
  CMDTYPES.AutoLogin, // 자동 로그인 - 1008
  CMDTYPES.Logout, // 로그아웃 - 1009
  CMDTYPES.PushTokenUpdate, // 푸시 토큰 업데이트 - 1003
])

function resolveFlowFromCmdType(cmdType: number): SentryApiFlow {
  if (PAYMENT_CMDTYPES.has(cmdType)) return 'payment'
  if (AUTH_CMDTYPES.has(cmdType)) return 'auth'
  if (JOIN_CMDTYPES.has(cmdType)) return 'join'
  if (CANCEL_CMDTYPES.has(cmdType)) return 'cancel'
  if (SESSION_CMDTYPES.has(cmdType)) return 'session'
  return 'other'
}

// ─────────────────────────────────────────────
// ErrCode 필터 / level 기준
// ─────────────────────────────────────────────

function errCodesFromEnum(enumObj: object): number[] {
  return Object.values(enumObj).filter((v): v is number => typeof v === 'number')
}

/** Warning level로 올릴 flow (other는 Info) */
const WARNING_FLOWS = new Set<SentryApiFlow>(['auth', 'join', 'cancel', 'session', 'payment'])

/** Sentry에 절대 전송하지 않는 에러코드 */
const NEVER_REPORT_ERR_CODES = new Set<number>([
  ERRCODE_SUCCESS,
  ERRCODES_COMMON.SESSION_ERROR,
  ERRCODES_COMMON.ERROR_LOGOUT,
  ERRCODES_COMMON.PIN_CHECK_ERROR,
  ERRCODES_COMMON.AUTH_ERROR,
  ERRCODES_VERSIONCHECK.NEED_UPDATE,
  ERRCODES_COMMON.SERVER_INTERNAL_ERROR,
])

/** 즉시 알림이 필요한 크리티컬 서버 에러코드 */
const CRITICAL_ERR_CODES = new Set<number>([
  ERRCODES_COMMON.SYSTEMS_ERROR,
  ERRCODES_COMMON.SYSTEM_MAINTENANCE,
])

/** 비즈니스상 정상적인 실패 — Sentry 미전송 */
const EXPECTED_ERR_CODES = new Set<number>([
  ...errCodesFromEnum(ERRCODES_OTPCERTREQUEST),
  ...errCodesFromEnum(ERRCODES_OTPCERTSUBMIT),
  ...errCodesFromEnum(ERRCODES_PURCHASE_VALID_CHECK),
  ...errCodesFromEnum(ERRCODES_PURCHASE_CANCEL),
  ...errCodesFromEnum(ERRCODES_PURCHASE_REFUND),
  ...errCodesFromEnum(ERRCODES_GIFT_VALID_CHECK),
  ...errCodesFromEnum(ERRCODES_WALLETCOUPON),
  ...errCodesFromEnum(ERRCODES_PASS_CERT),
])

// ─────────────────────────────────────────────
// 진입점 (fetcher)
// ─────────────────────────────────────────────

export function captureApiResponseError(header: { ErrCode: number; CmdType?: number }): void {
  const errCode = header.ErrCode

  // Sentry 미전송 에러
  if (NEVER_REPORT_ERR_CODES.has(errCode)) return
  if (EXPECTED_ERR_CODES.has(errCode)) return

  const cmdType = header.CmdType || 0
  const flow = resolveFlowFromCmdType(header.CmdType || 0)

  // 🔴 (error) 크리티컬 system 에러
  if (CRITICAL_ERR_CODES.has(errCode)) {
    captureError(
      toApiError(
        new Error(
          `[API] Critical server error | flow=${flow} cmdType=${cmdType} errCode=${errCode}`,
        ),
      ),
      {
        level: SENTRY_SEVERITY.Error,
        tags: { flow, cmdType: String(cmdType), errCode: String(errCode) },
      },
    )
    return
  }

  // 🟠 (warning) warning flow 에러
  if (WARNING_FLOWS.has(flow)) {
    if (EXPECTED_ERR_CODES.has(errCode)) return
    captureError(
      toApiError(
        new Error(`[API] Unexpected error | flow=${flow} cmdType=${cmdType} errCode=${errCode}`),
      ),
      {
        level: SENTRY_SEVERITY.Warning,
        tags: { flow, cmdType: String(cmdType), errCode: String(errCode) },
      },
    )
    return
  }

  // 🔵 (info) other flow 에러
  captureError(
    toApiError(
      new Error(`[API] Unexpected error | flow=${flow} cmdType=${cmdType} errCode=${errCode}`),
    ),
    {
      level: SENTRY_SEVERITY.Info,
      tags: { flow, cmdType: String(cmdType), errCode: String(errCode) },
    },
  )
}
